// Page d'accueil publique (vitrine) de Porinetia Compta AERH — design premium.
// Accessible sans connexion. Les visiteurs connectés sont redirigés :
//   - avec abonnement (ou admin) -> /tableau-de-bord
//   - sans abonnement -> /formules
//   - sans entreprise -> /onboarding
// HTML sémantique strict (un seul H1, H2 par section, H3 sous-éléments) pour le SEO.
// Profondeur via dégradés, grain, motif « tapa » et verre dépoli — transitions CSS uniquement.
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  FileText,
  PieChart,
  ShieldCheck,
  Users,
  GraduationCap,
  Check,
  ArrowRight,
  Mail,
  Phone,
  TrendingUp,
  Wallet,
  Sparkles,
} from "lucide-react";

const LOGO = "/logo_GAP.jpg";
const IMG_LAGON =
  "https://images.pexels.com/photos/34095820/pexels-photo-34095820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const fmt = (n) => Math.round(n || 0).toLocaleString("fr-FR");
const prixAnnuel = (mensuel) => Math.round(mensuel * 12 * 0.9);

const STATS = [
  "25 ans d'expertise locale",
  "TVA DICP intégrée",
  "Données chiffrées",
  "Support à Papeete",
];

const BENEFICES = [
  {
    icone: MapPin,
    titre: "Conçu pour la Polynésie",
    texte:
      "TVA à 0%, 5%, 13% et 16%, formulaire de déclaration DICP intégré, plan comptable adapté au contexte local. Pas un logiciel métropolitain adapté — un outil pensé ici, pour ici.",
  },
  {
    icone: FileText,
    titre: "Déclaration TVA DICP en un clic",
    texte:
      "Le formulaire officiel DICP est pré-rempli automatiquement à partir de vos saisies. Vérifiez, imprimez, déposez. Plus d'erreurs, plus de stress avant chaque échéance.",
  },
  {
    icone: PieChart,
    titre: "Votre situation financière en temps réel",
    texte:
      "Trésorerie, créances clients, dettes fournisseurs, TVA à reverser — tout visible en un coup d'œil sur votre tableau de bord, mis à jour après chaque saisie.",
  },
  {
    icone: ShieldCheck,
    titre: "Vos données sécurisées et sauvegardées",
    texte:
      "Vos données comptables sont chiffrées, sauvegardées automatiquement et accessibles à tout moment. Vous pouvez également télécharger une sauvegarde locale chiffrée.",
  },
  {
    icone: Users,
    titre: "Un support local qui vous connaît",
    texte:
      "Groupe AERH Polynésie accompagne les entreprises du fenua depuis plus de 25 ans. Notre équipe est joignable par email et téléphone, en français, depuis Papeete.",
  },
  {
    icone: GraduationCap,
    titre: "Formation incluse pour bien démarrer",
    texte:
      "Avant votre première saisie, un questionnaire évalue votre niveau. Si besoin, une formation de 3 heures avec un expert AERH vous garantit une prise en main réussie.",
  },
];

const ETAPES = [
  {
    num: "01",
    titre: "Créez votre compte",
    texte:
      "Inscription en 2 minutes. Renseignez les informations de votre entreprise — N° Tahiti, régime TVA, forme juridique.",
  },
  {
    num: "02",
    titre: "Évaluez votre niveau",
    texte:
      "Un questionnaire rapide évalue vos bases en comptabilité. Si besoin, nous vous accompagnons avec une formation dédiée.",
  },
  {
    num: "03",
    titre: "Choisissez votre formule",
    texte:
      "Solo, Équipe ou Structure — choisissez la formule adaptée à la taille de votre entreprise. 30 jours gratuits, sans carte.",
  },
  {
    num: "04",
    titre: "Gérez votre comptabilité",
    texte:
      "Saisissez vos écritures dans vos journaux, suivez votre trésorerie et éditez vos états comptables en quelques clics.",
  },
];

const FORMULES = [
  {
    code: "solo",
    libelle: "Solo",
    description: "TPE 1 à 5 personnes",
    prixMensuel: 3500,
    atouts: ["1 utilisateur", "Tous les journaux & états", "Exports CSV / XLS / PDF", "Déclaration TVA DICP"],
    populaire: false,
  },
  {
    code: "equipe",
    libelle: "Équipe",
    description: "PME 6 à 20 personnes",
    prixMensuel: 6500,
    atouts: ["Jusqu'à 5 utilisateurs", "Tout Solo inclus", "Support prioritaire", "Rapprochement bancaire"],
    populaire: true,
  },
  {
    code: "structure",
    libelle: "Structure",
    description: "PME 20 personnes et plus",
    prixMensuel: 12000,
    atouts: ["Utilisateurs illimités", "Multi-sociétés", "Tout Équipe inclus", "Accompagnement dédié"],
    populaire: false,
  },
];

// ----- Maquette stylisée du tableau de bord (palette AERH, sans image externe) -----
function DashboardMockup() {
  return (
    <div
      data-testid="hero-mockup"
      aria-hidden="true"
      className="relative w-full rounded-xl bg-white shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] overflow-hidden"
    >
      <div className="flex items-center gap-1.5 px-4 py-3 bg-[#1D3D3D]">
        <span className="h-2.5 w-2.5 rounded-full bg-white/30" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/30" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/30" />
        <span className="ml-3 text-xs font-medium text-white/80">Tableau de bord — Porinetia Compta AERH</span>
      </div>
      <div className="p-5 bg-white">
        <div className="grid grid-cols-3 gap-3">
          {[
            { l: "Trésorerie", v: "4 250 000", i: Wallet },
            { l: "Créances", v: "1 180 000", i: TrendingUp },
            { l: "TVA à reverser", v: "326 500", i: FileText },
          ].map((c) => (
            <div key={c.l} className="rounded-xl bg-[#E8F4F4]/60 p-3 ring-1 ring-[#1D3D3D]/5">
              <div className="flex items-center gap-1.5 text-[#4A8A8A]">
                <c.i className="h-3.5 w-3.5" />
                <span className="text-[10px] font-semibold uppercase tracking-wide">{c.l}</span>
              </div>
              <p className="mt-1 text-sm font-extrabold text-[#1D3D3D]">{c.v}</p>
              <p className="text-[10px] text-[#1D3D3D]/40">XPF</p>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-xl bg-[#E8F4F4]/40 p-4 ring-1 ring-[#1D3D3D]/5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-[#1D3D3D]">Évolution du chiffre d'affaires</span>
            <span className="text-[10px] font-semibold text-[#4A8A8A]">12 mois</span>
          </div>
          <div className="mt-3 flex items-end gap-1.5 h-24">
            {[40, 55, 48, 62, 70, 58, 75, 82, 68, 88, 95, 100].map((h, idx) => (
              <div
                key={idx}
                className="flex-1 rounded-t bg-gradient-to-t from-[#1D3D3D] to-[#4A8A8A]"
                style={{ height: `${h}%`, opacity: 0.45 + (idx / 12) * 0.55 }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, company, companyChargee, abonnement, estAdmin } = useAuth();
  const [periodicite, setPeriodicite] = useState("mensuel");

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    if (estAdmin) {
      navigate("/admin/abonnements", { replace: true });
    } else if (!companyChargee) {
      return;
    } else if (!company) {
      navigate("/onboarding", { replace: true });
    } else if (abonnement) {
      navigate("/tableau-de-bord", { replace: true });
    } else {
      navigate("/formules", { replace: true });
    }
  }, [user, company, companyChargee, abonnement, estAdmin, navigate]);

  return (
    <div data-testid="page-landing" className="min-h-screen bg-white text-[#1D3D3D] antialiased">
      {/* ===================== SECTION 1 — HEADER ===================== */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#1D3D3D]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img
              src={LOGO}
              alt="Groupe AERH Polynésie — Porinetia Compta AERH"
              className="h-8 sm:h-9 w-auto rounded shrink-0"
              width="36"
              height="36"
            />
            <div className="leading-tight min-w-0">
              <p className="text-sm sm:text-base font-extrabold tracking-tight truncate">Porinetia Compta AERH</p>
              <p className="hidden sm:block text-[11px] font-medium text-[#4A8A8A]">Polynésie française</p>
            </div>
          </div>
          <nav className="flex items-center gap-2 sm:gap-4 shrink-0">
            <Link
              to="/login"
              data-testid="header-login-link"
              className="hidden sm:inline-flex text-sm font-semibold text-[#1D3D3D] hover:text-[#4A8A8A] transition-colors"
            >
              Se connecter
            </Link>
            <Button
              asChild
              data-testid="header-register-btn"
              className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white font-semibold rounded-md transition-colors px-3 sm:px-4 text-sm"
            >
              <Link to="/register">
                <span className="sm:hidden">Commencer</span>
                <span className="hidden sm:inline">Commencer gratuitement</span>
              </Link>
            </Button>
          </nav>
        </div>
      </header>

      <main>
        {/* ===================== SECTION 2 — HERO ===================== */}
        <section
          aria-label="Présentation"
          className="bg-noise relative overflow-hidden bg-gradient-to-br from-[#1D3D3D] via-[#2D5A5A] to-[#1D3D3D]"
        >
          {/* Motif tapa en filigrane */}
          <div className="bg-tapa absolute inset-0 opacity-[0.07] pointer-events-none" aria-hidden="true" />
          {/* Halo d'accent */}
          <div
            className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#4A8A8A]/30 blur-3xl pointer-events-none"
            aria-hidden="true"
          />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-28 md:pb-36 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 min-w-0 fenua-stagger">
              <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#4A8A8A]">
                <span className="h-px w-8 bg-[#4A8A8A]" /> La comptabilité du fenua
              </p>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
                La comptabilité du fenua,{" "}
                <span className="bg-gradient-to-r from-[#4A8A8A] to-[#E8F4F4] bg-clip-text text-transparent">
                  enfin simple.
                </span>
              </h1>
              <p className="mt-6 text-base lg:text-lg leading-relaxed text-[#E8F4F4]/90 max-w-xl">
                Porinetia Compta AERH est le premier logiciel comptable conçu spécifiquement pour les TPE et PME de
                Polynésie française. Gérez vos factures, votre trésorerie et vos déclarations TVA DICP depuis un seul
                outil, sans être comptable.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  asChild
                  data-testid="hero-register-btn"
                  className="h-12 px-8 bg-[#E8F4F4] hover:bg-white text-[#1D3D3D] font-bold text-base rounded-md transition-colors w-full sm:w-auto shadow-lg"
                >
                  <Link to="/register">Essayer gratuitement 30 jours</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  data-testid="hero-formules-btn"
                  className="h-12 px-8 bg-transparent border-2 border-white/40 text-white hover:bg-white/10 hover:border-white font-semibold text-base rounded-md transition-colors w-full sm:w-auto"
                >
                  <a href="#formules">Voir les formules</a>
                </Button>
              </div>
              <p className="mt-4 text-sm text-[#E8F4F4]/70">Aucune carte bancaire requise pendant l'essai.</p>
            </div>

            <div className="lg:col-span-6 min-w-0 relative">
              <div
                className="absolute inset-0 -m-6 rounded-3xl bg-[#4A8A8A]/30 blur-3xl pointer-events-none"
                aria-hidden="true"
              />
              <div className="relative rounded-2xl bg-white/10 backdrop-blur-2xl border border-white/20 p-3 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]">
                <DashboardMockup />
              </div>
            </div>
          </div>
        </section>

        {/* ===================== STATS STRIP ===================== */}
        <section aria-label="Points de confiance" className="relative z-20 -mt-14">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-[#4A8A8A] text-white shadow-2xl px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {STATS.map((s) => (
                <div key={s} className="flex flex-col items-center justify-center">
                  <Sparkles className="h-4 w-4 text-white/70 mb-1" />
                  <span className="text-sm font-semibold leading-tight">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== SECTION 3 — BÉNÉFICES ===================== */}
        <section aria-label="Bénéfices clés" className="bg-[#E8F4F4]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
            <div className="max-w-2xl">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#4A8A8A]">Pourquoi nous</p>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
                Pourquoi choisir Porinetia Compta AERH ?
              </h2>
              <p className="mt-4 text-base lg:text-lg text-[#2D5A5A]">
                Un logiciel comptable pensé pour Tahiti et l'ensemble du fenua, qui parle votre langue et connaît vos
                obligations fiscales.
              </p>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {BENEFICES.map((b) => {
                const Icone = b.icone;
                return (
                  <article
                    key={b.titre}
                    data-testid={`benefice-${b.titre}`}
                    className="group bg-white rounded-2xl border-t-4 border-[#4A8A8A] p-8 shadow-[0_4px_24px_rgba(29,61,61,0.06)] transition-all duration-300 hover:shadow-[0_16px_44px_rgba(29,61,61,0.14)] hover:-translate-y-1 h-full flex flex-col"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2D5A5A] to-[#4A8A8A] flex items-center justify-center text-white mb-5 shadow-md transition-transform duration-300 group-hover:scale-110">
                      <Icone className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-bold tracking-tight">{b.titre}</h3>
                    <p className="mt-3 text-base leading-relaxed text-[#2D5A5A]">{b.texte}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ===================== SECTION 4 — COMMENT ÇA MARCHE ===================== */}
        <section aria-label="Comment ça marche" className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
            <div className="text-center max-w-2xl mx-auto">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#4A8A8A]">En toute simplicité</p>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Démarrer en 4 étapes</h2>
            </div>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
              {ETAPES.map((e, i) => (
                <div key={e.num} data-testid={`etape-${e.num}`} className="relative pt-8">
                  <span
                    className="absolute -top-2 left-0 text-7xl font-black text-[#E8F4F4] select-none pointer-events-none"
                    aria-hidden="true"
                  >
                    {e.num}
                  </span>
                  {i < ETAPES.length - 1 && (
                    <span
                      className="hidden lg:block absolute top-4 left-16 right-[-1.5rem] h-px bg-[#4A8A8A]/30"
                      aria-hidden="true"
                    />
                  )}
                  <div className="relative">
                    <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#1D3D3D] text-white text-sm font-bold">
                      {i + 1}
                    </span>
                    <h3 className="mt-4 text-xl font-bold tracking-tight">{e.titre}</h3>
                    <p className="mt-2 text-base leading-relaxed text-[#2D5A5A]">{e.texte}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== SECTION 5 — FORMULES ===================== */}
        <section id="formules" aria-label="Les formules" className="bg-[#E8F4F4] scroll-mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
            <div className="text-center max-w-2xl mx-auto">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#4A8A8A]">Tarifs en XPF</p>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
                Des formules adaptées à chaque taille d'entreprise
              </h2>
              <p className="mt-4 text-base lg:text-lg text-[#2D5A5A]">
                Polynésie française · résiliable à tout moment · 30 jours gratuits sans carte.
              </p>
            </div>

            <div className="mt-8 flex justify-center">
              <div className="inline-flex bg-white rounded-full p-1 border border-[#4A8A8A]/30 shadow-sm">
                <button
                  data-testid="landing-toggle-mensuel"
                  onClick={() => setPeriodicite("mensuel")}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                    periodicite === "mensuel" ? "bg-[#1D3D3D] text-white" : "text-[#1D3D3D]"
                  }`}
                >
                  Mensuel
                </button>
                <button
                  data-testid="landing-toggle-annuel"
                  onClick={() => setPeriodicite("annuel")}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                    periodicite === "annuel" ? "bg-[#1D3D3D] text-white" : "text-[#1D3D3D]"
                  }`}
                >
                  Annuel <span className="text-emerald-500">-10%</span>
                </button>
              </div>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto items-center">
              {FORMULES.map((f) => (
                <div
                  key={f.code}
                  data-testid={`landing-formule-${f.code}`}
                  className={`relative bg-white rounded-2xl p-7 flex flex-col transition-all duration-300 ${
                    f.populaire
                      ? "lg:scale-105 shadow-[0_20px_50px_-12px_rgba(29,61,61,0.3)] ring-2 ring-[#4A8A8A] z-10"
                      : "border border-[#4A8A8A]/20 shadow-sm hover:-translate-y-1"
                  }`}
                >
                  {f.populaire && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-gradient-to-r from-[#2D5A5A] to-[#4A8A8A] text-white text-xs font-semibold whitespace-nowrap shadow">
                      Le plus choisi
                    </span>
                  )}
                  <h3 className="text-lg font-black uppercase tracking-wide">{f.libelle}</h3>
                  <p className="mt-1 text-sm text-[#1D3D3D]/60 min-h-[40px]">{f.description}</p>
                  <div className="mt-4">
                    <span data-testid={`landing-prix-${f.code}`} className="text-4xl font-black">
                      {periodicite === "mensuel" ? fmt(f.prixMensuel) : fmt(prixAnnuel(f.prixMensuel))}
                    </span>
                    <span className="text-sm font-medium text-[#1D3D3D]/70">
                      {" "}
                      XPF / {periodicite === "mensuel" ? "mois" : "an"}
                    </span>
                    {periodicite === "annuel" && (
                      <p className="mt-1 text-xs text-emerald-600 font-semibold">
                        soit {fmt(Math.round(prixAnnuel(f.prixMensuel) / 12))} XPF/mois — économisez 10%
                      </p>
                    )}
                  </div>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {f.atouts.map((a) => (
                      <li key={a} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-[#4A8A8A] shrink-0 mt-0.5" /> {a}
                      </li>
                    ))}
                  </ul>
                  <Button
                    asChild
                    data-testid={`landing-choisir-${f.code}`}
                    className={`mt-6 h-11 font-semibold rounded-md transition-colors ${
                      f.populaire
                        ? "bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white"
                        : "bg-[#E8F4F4] hover:bg-[#4A8A8A]/20 text-[#1D3D3D] border border-[#4A8A8A]/30"
                    }`}
                  >
                    <Link to="/register">Commencer l'essai gratuit</Link>
                  </Button>
                  <p className="mt-3 text-center text-xs text-[#1D3D3D]/55">30 jours gratuits — sans carte bancaire</p>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/comparatif-formules"
                data-testid="landing-comparatif-link"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1D3D3D] hover:text-[#4A8A8A] transition-colors"
              >
                Comparer les formules en détail <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* ===================== SECTION 6 — CONFIANCE / AERH ===================== */}
        <section
          aria-label="À propos de Groupe AERH Polynésie"
          className="bg-noise relative overflow-hidden bg-[#1D3D3D] text-white"
        >
          <img
            src={IMG_LAGON}
            alt="Lagon turquoise de Polynésie française"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1D3D3D] via-[#1D3D3D]/90 to-[#1D3D3D]/60" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
            <div className="min-w-0">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#4A8A8A]">L'éditeur</p>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
                Édité par Groupe AERH Polynésie
              </h2>
              <p className="mt-6 text-base lg:text-lg leading-relaxed text-white/85">
                Groupe AERH Polynésie accompagne les entreprises de Polynésie française depuis plus de 25 ans en conseil
                RH, formation, recrutement et paie. Porinetia Compta AERH est notre réponse aux besoins comptables des TPE et
                PME du fenua — un outil local, maintenu et supporté depuis Papeete.
              </p>
            </div>
            <div className="min-w-0 rounded-2xl bg-white/10 backdrop-blur-2xl border border-white/20 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
              <img
                src={LOGO}
                alt="Groupe AERH Polynésie — Porinetia Compta AERH"
                className="h-14 w-auto rounded bg-white p-1"
                width="56"
                height="56"
              />
              <h3 className="mt-5 text-lg font-bold">Groupe AERH Polynésie</h3>
              <ul className="mt-4 space-y-3 text-base">
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#4A8A8A] shrink-0 mt-0.5" />
                  <span>10 Rue Georges LAGARDE — Papeete</span>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-[#4A8A8A] shrink-0" />
                  <a
                    href="mailto:contact@groupeaerhpolynesie.com"
                    className="hover:text-[#4A8A8A] transition-colors break-all"
                  >
                    contact@groupeaerhpolynesie.com
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-[#4A8A8A] shrink-0" />
                  <a href="tel:+68987737815" className="hover:text-[#4A8A8A] transition-colors">
                    87 73 78 15
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* ===================== SECTION 7 — CTA FINAL ===================== */}
        <section
          aria-label="Commencer maintenant"
          className="bg-noise relative overflow-hidden bg-gradient-to-br from-[#2D5A5A] to-[#1D3D3D]"
        >
          <div className="bg-tapa absolute inset-0 opacity-[0.06] pointer-events-none" aria-hidden="true" />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white">
              Prêt à simplifier votre comptabilité ?
            </h2>
            <p className="mt-5 text-base lg:text-lg leading-relaxed text-[#E8F4F4]/90">
              Rejoignez les entreprises du fenua qui font confiance à Porinetia Compta AERH pour tenir leur comptabilité
              simplement et rigoureusement.
            </p>
            <Button
              asChild
              data-testid="cta-final-register-btn"
              className="mt-8 h-12 px-8 bg-[#E8F4F4] hover:bg-white text-[#1D3D3D] font-bold text-base rounded-md transition-colors w-full sm:w-auto shadow-lg"
            >
              <Link to="/register">Commencer mon essai gratuit</Link>
            </Button>
            <p className="mt-4 text-sm text-[#E8F4F4]/70">
              30 jours gratuits — Aucune carte bancaire requise — Accès immédiat
            </p>
          </div>
        </section>
      </main>

      {/* ===================== FOOTER ===================== */}
      <footer className="bg-[#1D3D3D] text-[#E8F4F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          <div className="flex items-center gap-3">
            <img
              src={LOGO}
              alt="Groupe AERH Polynésie — Porinetia Compta AERH"
              className="h-9 w-auto rounded bg-white p-0.5"
              width="36"
              height="36"
            />
            <div className="leading-tight">
              <p className="text-base font-extrabold tracking-tight text-white">Porinetia Compta AERH</p>
              <p className="text-xs text-[#E8F4F4]/60">© 2026 Groupe AERH Polynésie — Tous droits réservés</p>
            </div>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 md:justify-end text-sm">
            <Link to="/mentions-legales" className="text-[#E8F4F4]/70 hover:text-white transition-colors">
              Mentions légales
            </Link>
            <Link
              to="/politique-confidentialite"
              className="text-[#E8F4F4]/70 hover:text-white transition-colors"
            >
              Politique de confidentialité
            </Link>
            <Link to="/cgu" className="text-[#E8F4F4]/70 hover:text-white transition-colors">
              CGU/CGV
            </Link>
            <Link
              to="/contact"
              data-testid="footer-contact-link"
              className="text-[#E8F4F4]/70 hover:text-white transition-colors"
            >
              Contact
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
